import React, { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Clock, Github, MoreHorizontal, Plus, Star } from 'lucide-react';
import { useMoveTaskMutation, type Task, type TaskPerson } from '@/client/api/tasks/tasks';
import {
  Avatar,
  PriorityPill,
  between,
  fmtDay,
  groupBySection,
  isClosed,
  isDueSoon,
  isOverdue,
  type SectionGroup,
  type TaskViewProps,
} from './taskUi';

const colId = (id: string | null) => `col:${id ?? ''}`;

const TaskCard: React.FC<{ task: Task; people?: TaskPerson[]; selected?: boolean; onOpen?: (t: Task) => void }> = ({
  task,
  people,
  selected,
  onOpen,
}) => {
  const closed = isClosed(task.status);
  const overdue = isOverdue(task);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(task)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) onOpen?.(task);
      }}
      className={`cursor-pointer rounded border border-gray-200 bg-white px-2.5 py-2 text-sm shadow-sm hover:border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
        task.unread ? 'border-l-[3px] border-l-blue-500' : ''
      } ${selected ? 'ring-2 ring-blue-500' : ''}`}
    >
      <div className="flex items-start gap-2">
        <Avatar people={people} uid={task.ownerUid} size={26} />
        <div className="min-w-0 flex-1">
          <div className={`line-clamp-3 break-words ${closed ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{task.title}</div>
          {task.progress != null && (
            <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-500">
              <span>Progress:</span>
              <div
                className="h-1.5 flex-1 rounded-full bg-gray-200"
                role="progressbar"
                aria-valuenow={task.progress}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className="h-full rounded-full bg-blue-500" style={{ width: `${Math.min(100, Math.max(0, task.progress))}%` }} />
              </div>
            </div>
          )}
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
            <span className={overdue ? 'font-medium text-red-600' : ''}>
              T{task.number}
              {task.dueDate ? ` · ${fmtDay(task.dueDate)}` : ''}
            </span>
            {isDueSoon(task) && <Clock className="h-3.5 w-3.5 text-amber-500" aria-label="Due soon" />}
            {task.githubIssues.length > 0 && <Github className="h-3.5 w-3.5 text-gray-400" aria-label="Linked GitHub issue" />}
            {task.starred && <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" aria-label="Starred" />}
            {task.priority !== 'none' && <PriorityPill priority={task.priority} className="ml-auto" />}
          </div>
        </div>
      </div>
    </div>
  );
};

const SortableCard: React.FC<{ task: Task; people?: TaskPerson[]; selected: boolean; onOpen: (t: Task) => void; disabled: boolean }> = ({
  task,
  people,
  selected,
  onOpen,
  disabled,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id, disabled });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0) scaleX(${transform.scaleX}) scaleY(${transform.scaleY})` : undefined, transition, opacity: isDragging ? 0.4 : 1 }}
    >
      <TaskCard task={task} people={people} selected={selected} onOpen={onOpen} />
    </div>
  );
};

const NewTask: React.FC<{ me: number | null | undefined; people?: TaskPerson[]; onCreate: (title: string) => Promise<void> }> = ({
  me,
  people,
  onCreate,
}) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [err, setErr] = useState('');

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 flex items-center gap-1 px-1 text-sm text-gray-500 hover:text-gray-800"
      >
        <Plus className="h-4 w-4" /> New Task
      </button>
    );
  }
  const submit = async () => {
    const t = title.trim();
    if (!t) return;
    setErr('');
    try {
      await onCreate(t);
      setTitle('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not create task');
    }
  };
  return (
    <div className="mt-2 rounded border border-blue-500 bg-white p-2 shadow-sm">
      <div className="flex items-start gap-2">
        <Avatar people={people} uid={me ?? null} size={26} />
        <input
          autoFocus
          value={title}
          placeholder="Enter task title"
          aria-label="Enter task title"
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
            if (e.key === 'Escape') {
              setOpen(false);
              setTitle('');
              setErr('');
            }
          }}
          onBlur={() => {
            if (!title.trim()) {
              setOpen(false);
              setErr('');
            }
          }}
          className="min-w-0 flex-1 bg-transparent text-sm placeholder:text-gray-400 focus:outline-none"
        />
      </div>
      {err && <div className="mt-1 text-xs text-red-600">{err}</div>}
    </div>
  );
};

const Column: React.FC<{
  group: SectionGroup;
  props: TaskViewProps;
  dragDisabled: boolean;
}> = ({ group, props, dragDisabled }) => {
  const { setNodeRef } = useDroppable({ id: colId(group.id) });
  const people = props.meta?.people;
  return (
    <section className="flex max-h-full w-[280px] shrink-0 flex-col rounded-lg bg-gray-100 p-2">
      <header className="flex items-center justify-between px-1 pb-2">
        <h3 className="text-sm">
          <span className="font-bold text-gray-900">{group.name}</span> <span className="text-gray-400">{group.tasks.length}</span>
        </h3>
        <button type="button" aria-label="Section options" className="rounded p-0.5 text-gray-400 hover:bg-gray-200">
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </header>
      <div ref={setNodeRef} className="min-h-[8px] flex-1 space-y-2 overflow-y-auto">
        <SortableContext items={group.tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {group.tasks.map((t) => (
            <SortableCard key={t.id} task={t} people={people} selected={props.selectedId === t.id} onOpen={props.onOpen} disabled={dragDisabled} />
          ))}
        </SortableContext>
      </div>
      <NewTask
        me={props.meta?.me}
        people={people}
        onCreate={(title) => props.onCreate({ title, sectionId: group.id })}
      />
    </section>
  );
};

export default function CardsView(props: TaskViewProps) {
  const { tasks, project, loading, meta } = props;
  const move = useMoveTaskMutation();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const serverGroups = useMemo(() => groupBySection(tasks, project), [tasks, project]);
  const [groups, setGroups] = useState<SectionGroup[]>(serverGroups);
  const [activeId, setActiveId] = useState<string | null>(null);
  useEffect(() => setGroups(serverGroups), [serverGroups]);

  const findGroup = (gs: SectionGroup[], id: string) =>
    gs.find((g) => colId(g.id) === id) ?? gs.find((g) => g.tasks.some((t) => t.id === id));
  const activeTask = activeId ? groups.flatMap((g) => g.tasks).find((t) => t.id === activeId) : undefined;
  const multi = !!project;

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));

  // Move the card into the hovered column while dragging, so the preview follows the pointer.
  const onDragOver = ({ active, over }: DragOverEvent) => {
    if (!over || !multi) return;
    const a = String(active.id);
    const o = String(over.id);
    setGroups((gs) => {
      const from = findGroup(gs, a);
      const to = findGroup(gs, o);
      if (!from || !to || from === to) return gs;
      const task = from.tasks.find((t) => t.id === a)!;
      const overIdx = to.tasks.findIndex((t) => t.id === o);
      const idx = overIdx < 0 ? to.tasks.length : overIdx;
      return gs.map((g) =>
        g === from
          ? { ...g, tasks: g.tasks.filter((t) => t.id !== a) }
          : g === to
            ? { ...g, tasks: [...g.tasks.slice(0, idx), task, ...g.tasks.slice(idx)] }
            : g,
      );
    });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over) {
      setGroups(serverGroups);
      return;
    }
    const a = String(active.id);
    const o = String(over.id);
    const g = findGroup(groups, a);
    if (!g) return;
    let list = g.tasks;
    const from = list.findIndex((t) => t.id === a);
    const to = list.findIndex((t) => t.id === o);
    if (to >= 0 && from !== to) list = arrayMove(list, from, to);
    const i = list.findIndex((t) => t.id === a);
    const position = between(list[i - 1]?.position, list[i + 1]?.position);
    const moved = { ...list[i], position, sectionId: g.id };
    const next = list.map((t, k) => (k === i ? moved : t));
    setGroups((gs) => gs.map((x) => (x === g ? { ...x, tasks: next } : x)));
    const orig = tasks.find((t) => t.id === a);
    if (orig && orig.sectionId === g.id && orig.position === position) return;
    move.mutate({ id: a, sectionId: g.id, position });
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="flex h-full gap-3 overflow-x-auto p-3">
        <div className="w-[280px] shrink-0 space-y-2 rounded-lg bg-gray-100 p-2" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded bg-gray-200" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setActiveId(null);
        setGroups(serverGroups);
      }}
    >
      <div className="flex h-full items-start gap-3 overflow-x-auto p-3">
        {groups.map((g) => (
          <Column key={g.id ?? 'none'} group={g} props={props} dragDisabled={false} />
        ))}
      </div>
      <DragOverlay>{activeTask ? <TaskCard task={activeTask} people={meta?.people} /> : null}</DragOverlay>
    </DndContext>
  );
}
